import { Component, OnDestroy, ViewChild, OnInit } from "@angular/core";
import {
  NavController,
  Events,
  NavParams,
  ModalController,
  PopoverController
} from "ionic-angular";
import { Store } from "@ngrx/store";
import { AppState } from "../../app/models/app.state";
import { FhirService } from "../../app/services/fhir.service";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs/Subscription";
import { UserState } from "../../app/store/reducers/user.reducer";
import { DashboardPage } from "../dashboard.page/dashboard.page";
import { DashboardService } from "../../app/services/dashboard.service";
import { AdminService } from "../../app/services/admin.service";
import {
  DIAPROFIL_QUESTIONNAIRE_UUID,
  Sex,
  diabetesTypes,
  DanishMunicipality,
  activeTaskStatus,
  PatientListingColumnType
} from "../../app/models/types";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { ManageTaskModal } from "../../modals/manage-task.modal/manage-task.modal";
import {
  DiaprofilPatient,
  IDiaprofilPatient
} from "../../app/models/diaprofil-patient";
import { QuestionnaireResponseService } from "@zitelab/ionic-fhir-questionnaires";
import { ManagePatientModal } from "../../modals/manage-patient.modal/manage-patient.modal";
import { SelectPatientListingColumns } from "./select-patient-listing-columns.component/select-patient-listing-columns.component";
import { PatientPage } from "../patient.page/patient.page";
import { DiaprofilProTask } from "../../app/models/diaprofil-pro-task";

@Component({
  selector: "patient-listing-page",
  templateUrl: "patient-listing.page.html"
})
export class PatientListingPage implements OnInit, OnDestroy {
  @ViewChild("patientListing") table: any;
  userSubscription: Subscription;
  userState: UserState;
  today = new Date();
  patients: DiaprofilPatient[] = [];
  tableFilters: {
    ssn: string;
    name: string;
    sex: Sex;
    diabetesTypes: diabetesTypes[];
    pumpUser: boolean;
    municipality: DanishMunicipality[];
  } = {
    ssn: null,
    name: null,
    sex: null,
    diabetesTypes: null,
    pumpUser: null,
    municipality: null
  };
  localizedTableMessages: { [key: string]: string };
  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public events: Events,
    public store: Store<AppState>,
    public fhir: FhirService,
    public questionnaireResponse: QuestionnaireResponseService,
    public dashboard: DashboardService,
    public admin: AdminService,
    public translate: TranslateService,
    public modalCtrl: ModalController,
    public popoverCtrl: PopoverController
  ) {
    this.userSubscription = this.store
      .select(state => state.user)
      .subscribe(user => (this.userState = user));
    this.localizedTableMessages = {
      // Message to show when array is presented
      // but contains no values
      emptyMessage: translate.instant(
        _i18n_str("PATIENT_LISTING.MESSAGES.NO_DATA_TO_DISPLAY")
      ),
      // Footer total message
      totalMessage: translate.instant(
        _i18n_str("PATIENT_LISTING.MESSAGES.TOTAL")
      )
    };
  }

  ngOnInit() {
    this.refreshListing();
    this.store
      .select(state => state.patients)
      .subscribe(patients => {
        // We don't want to create patient objects here, so we delay filtering so we can use from admin service
        setTimeout(() => {
          this.filterRows(this.tableFilters);
        }, 200);
      });
  }

  toPatientPage(patient: DiaprofilPatient) {
    const navParams = {
      patient: patient
    };
    this.navCtrl.push(PatientPage, navParams, { animate: false });
  }

  toDashboardPage(
    patient: DiaprofilPatient,
    response: fhir.QuestionnaireResponse = null,
    proTask: DiaprofilProTask = null
  ) {
    if (response === null) {
      response = patient.latestResponse; // We default to the latest response
    }
    if (proTask === null) {
      proTask = new DiaprofilProTask(
        patient.latestCompletedProTask,
        this.translate,
        this.questionnaireResponse,
        this.dashboard,
        patient
      ); // We default to the latest response
    }
    if (this.dashboard.widgets === null) {
      const questionnaireResource = this.fhir.resolveReference(
        `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
      ) as fhir.Questionnaire;
      this.dashboard.setGenericProWidgets(questionnaireResource);
    }
    const extractedAnswers = this.questionnaireResponse.extractAnswersFromQuestionnaireResponse(
      response
    );
    const flattenedAnswers = this.questionnaireResponse.flattenQuestionnaireAnswers(
      extractedAnswers
    );
    const navParams = {
      responseResource: response,
      proTask: proTask,
      patient: patient
    };
    this.navCtrl.push(DashboardPage, navParams, { animate: false });
  }

  refreshListing() {
    this.admin.getPatients(this.userState.organizationUuid);
    this.admin.getHcps(this.userState.organizationUuid);
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
  }

  onDetailToggle(event) {}

  toggleExpandRow(row) {
    this.table.rowDetail.toggleExpandRow(row);
  }

  triggerTaskModal(patient: DiaprofilPatient, task = null) {
    const modal = this.modalCtrl.create(
      ManageTaskModal,
      { patient, task, user: this.userState },
      { cssClass: "medium-modal" }
    );
    modal.present();
  }

  triggerManagePatientModal($event, patient?: IDiaprofilPatient) {
    const navParams =
      patient !== undefined
        ? { patient, modalType: "EDIT" }
        : { modalType: "CREATE" };
    const modal = this.modalCtrl.create(ManagePatientModal, navParams, {
      cssClass: "large-modal",
      enableBackdropDismiss: false
    });
    modal.present();
  }

  isColumnEnabled(columnName: PatientListingColumnType) {
    const userDisplayedColumns = this.userState
      .displayedColumnsInPatientListing;
    const enabledColumns =
      userDisplayedColumns !== null && userDisplayedColumns !== undefined
        ? this.userState.displayedColumnsInPatientListing
        : this.admin.orgConfig.ffDefaultColumnsInPatientListing;
    if (this.admin.orgConfig.ffDefaultColumnsInPatientListing != null) {
      return enabledColumns.includes(columnName);
    } else {
      return true;
    }
  }

  presentColumnSelectionPopover(event) {
    const popover = this.popoverCtrl.create(SelectPatientListingColumns);
    popover.present({
      ev: event
    });
  }

  canCreateNewTask(patient, value) {
    const isEmptyValue = value === "";
    let consentCondition: boolean = true;
    const strictMode = this.admin.orgConfig.evalPatientConsentType === "STRICT";
    if (strictMode) {
      consentCondition = patient.evalConsent !== "NO_I_DO_NOT_CONSENT";
    }
    return isEmptyValue && consentCondition;
  }

  calculateActiveTaskBadgeColor(patient: DiaprofilPatient) {
    const mapping: { [key in activeTaskStatus]: string } = {
      NORMAL: "primary",
      CLOSE_TO_EXPIRATION: "alert",
      EXPIRED: "danger"
    };
    return mapping[patient.statusOfActiveTask] || "light";
  }

  filterRows(filterValues, allPatients = this.admin.patients) {
    const filters = {
      ssn: (patient: DiaprofilPatient, filterBy: string) =>
        patient.socialSecurityNumber.indexOf(filterBy.replace("-", "")) > -1,
      name: (patient: DiaprofilPatient, filterBy: string) => {
        return (
          patient.fullName
            .toLocaleLowerCase()
            .indexOf(filterBy.toLocaleLowerCase()) > -1
        );
      }
    };
    const activeFilters = Object.keys(filters).filter(
      (keyToFilterOn, index) => {
        const filterValue = filterValues[keyToFilterOn];
        return filterValue !== null;
      }
    );
    const runFilters = (patient: DiaprofilPatient) => {
      let result = true;
      for (const filter of activeFilters) {
        const filterFunction = filters[filter];
        const filterBy = filterValues[filter];
        const filterResult = filterFunction(patient, filterBy);
        if (filterResult === false) {
          result = false;
          break;
        }
      }
      return result;
    };
    const filteredPatients = allPatients.filter(patient => runFilters(patient));
    this.patients = filteredPatients;
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-listing.page/patient-listing.page.ts